/**
 * YUMIX — Coach Tips Registry (Iter. 395)
 *
 * Single source of truth for all <YumixTip> content across the app.
 * Each tip is keyed by a stable `id` and contains FR / EN / ES variants
 * for both title and text (JSX allowed in text for rich formatting).
 *
 * ➜ ZERO HARDCODED COACH TEXT in components. Always import from here.
 *
 * Validated by `scripts/tips-audit.js` (CI pre-commit hook):
 *   - Every <YumixTip id="..."> must reference a key in this registry.
 *   - Every key in this registry must have all 3 locales (fr/en/es).
 *   - Text length must stay under MAX_TIP_LENGTH (defensively against bloat).
 *
 * Usage (in any component):
 *   import { useCoachTip } from '../constants/coachTips';
 *   const tip = useCoachTip('exercise.tempo');
 *   <YumixTip {...tip} />
 *
 * Or imperatively (e.g., outside React):
 *   import { getCoachTip } from '../constants/coachTips';
 *   const tip = getCoachTip('exercise.tempo', 'fr');
 */
import React from 'react';
import { useLanguage } from '../context/AppContext';

/** @constant Soft character ceiling — `tips-audit.js` will warn beyond this. */
export const MAX_TIP_LENGTH = 600;

/**
 * @typedef CoachTipLocale
 * @property {string} title
 * @property {string|React.ReactNode} text
 */

/**
 * @typedef CoachTip
 * @property {string} id
 * @property {{fr: CoachTipLocale, en: CoachTipLocale, es: CoachTipLocale}} locales
 */

/* eslint-disable react/jsx-key */
/** @type {Record<string, CoachTip>} */
export const COACH_TIPS = {
  // ────────────────────────────────────────────────────────────────────
  // ENTRAÎNEMENT — Tempo des exercices
  // ────────────────────────────────────────────────────────────────────
  'exercise.tempo': {
    id: 'exercise-tempo',
    locales: {
      fr: {
        title: 'Le tempo expliqué',
        text: (
          <span>
            <strong>3 chiffres = 3 phases du mouvement</strong> (en secondes).<br/>
            <span className="text-primary">2-0-2</span> = 2s phase négative (descente), 0s pause, 2s phase concentrique (poussée).<br/>
            Un tempo lent <span className="text-primary">(4-0-1)</span> augmente le temps sous tension → plus d'hypertrophie. Un tempo rapide <span className="text-primary">(2-0-2)</span> = explosivité.
          </span>
        ),
      },
      en: {
        title: 'Tempo explained',
        text: (
          <span>
            <strong>3 numbers = 3 phases of the lift</strong> (in seconds).<br/>
            <span className="text-primary">2-0-2</span> = 2s eccentric (down), 0s pause, 2s concentric (up).<br/>
            Slow tempo <span className="text-primary">(4-0-1)</span> = more time under tension → hypertrophy. Fast tempo <span className="text-primary">(2-0-2)</span> = power.
          </span>
        ),
      },
      es: {
        title: 'El tempo explicado',
        text: (
          <span>
            <strong>3 cifras = 3 fases del movimiento</strong> (en segundos).<br/>
            <span className="text-primary">2-0-2</span> = 2s excéntrica (bajada), 0s pausa, 2s concéntrica (empuje).<br/>
            Tempo lento <span className="text-primary">(4-0-1)</span> = más tiempo bajo tensión → hipertrofia. Tempo rápido = explosividad.
          </span>
        ),
      },
    },
  },

  // ────────────────────────────────────────────────────────────────────
  // ENTRAÎNEMENT — RIR / Intensité des séries
  // ────────────────────────────────────────────────────────────────────
  'workout.rir': {
    id: 'workout-rir-intensity',
    locales: {
      fr: {
        title: 'RIR / Intensité',
        text: (
          <span>
            <strong>RIR = Reps In Reserve</strong> — le nombre de répétitions que tu aurais ENCORE pu faire à la fin de ta série.<br/>
            <span className="text-primary">RIR 0</span> = échec complet (rien dans le réservoir).<br/>
            <span className="text-primary">RIR 2</span> = 2 reps restantes dans le moteur.<br/>
            🎯 Cible idéale pour l'hypertrophie : <strong>RIR 1-3</strong>. Au-delà, tu es trop frais. Trop d'échecs (RIR 0 répétés) = ton coach YUMIX réduira la charge automatiquement.
          </span>
        ),
      },
      en: {
        title: 'RIR / Intensity',
        text: (
          <span>
            <strong>RIR = Reps In Reserve</strong> — how many reps you could STILL have done at the end of the set.<br/>
            <span className="text-primary">RIR 0</span> = full failure.<br/>
            <span className="text-primary">RIR 2</span> = 2 reps left in the tank.<br/>
            🎯 Sweet spot for hypertrophy: <strong>RIR 1-3</strong>. Too many RIR=0 logs trigger auto-deload from your AI coach.
          </span>
        ),
      },
      es: {
        title: 'RIR / Intensidad',
        text: (
          <span>
            <strong>RIR = Reps In Reserve</strong> — cuántas reps más habrías podido hacer al final de la serie.<br/>
            <span className="text-primary">RIR 0</span> = fallo total.<br/>
            <span className="text-primary">RIR 2</span> = 2 reps en el tanque.<br/>
            🎯 Zona ideal para hipertrofia: <strong>RIR 1-3</strong>. Demasiados RIR=0 → tu coach YUMIX hace deload automático.
          </span>
        ),
      },
    },
  },

  // ────────────────────────────────────────────────────────────────────
  // CALCULATEUR DE MACROS — NEAT / Profil professionnel
  // ────────────────────────────────────────────────────────────────────
  'macros.neat': {
    id: 'macros-neat-job-profile',
    locales: {
      fr: {
        title: 'NEAT — Le calorie tueur silencieux',
        text: (
          <span>
            <strong>NEAT</strong> = Non-Exercise Activity Thermogenesis. Toutes les calories que tu brûles en dehors du sport (debout, marcher, porter, gesticuler).<br/><br/>
            <span className="text-primary">Bureau (×1.0)</span> : assis 8h/jour → budget calorique standard.<br/>
            <span className="text-primary">Chef en cuisine (×1.3)</span> : debout + chaleur + charges → +400 kcal/jour. Si tu ne le corriges pas, le système te met en déficit caché et tu craques.<br/><br/>
            🎯 Ce facteur protège les <strong>travailleurs physiques</strong> des régimes-famine et calibre les portions pour les <strong>sédentaires</strong>.
          </span>
        ),
      },
      en: {
        title: 'NEAT — The silent calorie killer',
        text: (
          <span>
            <strong>NEAT</strong> = Non-Exercise Activity Thermogenesis. All the calories you burn outside training (standing, walking, lifting, fidgeting).<br/><br/>
            <span className="text-primary">Desk job (×1.0)</span>: sitting 8h/day → standard budget.<br/>
            <span className="text-primary">Kitchen chef (×1.3)</span>: standing + heat + lifting → +400 kcal/day. Skip this and the system runs a hidden deficit, you crash.<br/><br/>
            🎯 Protects <strong>physical workers</strong> from starvation diets and right-sizes portions for the <strong>sedentary</strong>.
          </span>
        ),
      },
      es: {
        title: 'NEAT — El asesino silencioso de calorías',
        text: (
          <span>
            <strong>NEAT</strong> = Termogénesis de Actividad No Asociada al Ejercicio. Calorías quemadas fuera del deporte (de pie, caminar, cargar).<br/><br/>
            <span className="text-primary">Oficina (×1.0)</span>: 8h sentado → presupuesto estándar.<br/>
            <span className="text-primary">Chef de cocina (×1.3)</span>: de pie + calor + cargas → +400 kcal/día.<br/><br/>
            🎯 Protege a los <strong>trabajadores físicos</strong> de dietas hambrunas.
          </span>
        ),
      },
    },
  },

  // ────────────────────────────────────────────────────────────────────
  // CALCULATEUR DE MACROS — NEAT (variante "settings principal")
  // Affiché dans Settings.js → carte Activité (Iter. 394).
  // ────────────────────────────────────────────────────────────────────
  'macros.neat.settings': {
    id: 'settings-neat-job-profile',
    locales: {
      fr: {
        title: 'NEAT — Le calorie tueur silencieux',
        text: (
          <span>
            <strong>NEAT</strong> = calories brûlées hors sport (debout, marcher, charges).<br/><br/>
            <span className="text-primary">Bureau (×1.0)</span> : budget standard.<br/>
            <span className="text-primary">Chef en cuisine (×1.3)</span> : +30% kcal/jour. Si tu ne le corriges pas → déficit caché → tu craques.<br/><br/>
            🎯 Change ce champ → tes macros sont <strong>recalculées instantanément</strong> au moment d'enregistrer.
          </span>
        ),
      },
      en: {
        title: 'NEAT — Silent calorie killer',
        text: (
          <span>
            <strong>NEAT</strong> = calories burned outside training (standing, walking, lifting).<br/><br/>
            <span className="text-primary">Desk job (×1.0)</span> vs <span className="text-primary">Kitchen chef (×1.3)</span> = +30% kcal/day. Skip this and physical workers get starved.<br/><br/>
            🎯 Change this field → macros are <strong>recalculated instantly</strong> on save.
          </span>
        ),
      },
      es: {
        title: 'NEAT — Calorías silenciosas',
        text: (
          <span>
            <strong>NEAT</strong> = calorías quemadas fuera del deporte.<br/><br/>
            <span className="text-primary">Oficina (×1.0)</span> vs <span className="text-primary">Chef de cocina (×1.3)</span> = +30% kcal/día.<br/><br/>
            🎯 Al guardar, tus macros se recalculan al instante.
          </span>
        ),
      },
    },
  },
};
/* eslint-enable react/jsx-key */

/**
 * Lookup a coach tip by key, resolving the locale. Falls back to FR if
 * the requested language is missing (defensive).
 *
 * @param {string} key - dot-path key (e.g. 'exercise.tempo')
 * @param {('fr'|'en'|'es')} language
 * @returns {{id: string, title: string, text: React.ReactNode}|null}
 */
export function getCoachTip(key, language = 'fr') {
  const entry = COACH_TIPS[key];
  if (!entry) {
    // eslint-disable-next-line no-console
    console.warn(`[coachTips] Unknown key: ${key}`);
    return null;
  }
  const locale = entry.locales[language] || entry.locales.fr;
  return {
    id: entry.id,
    title: locale.title,
    text: locale.text,
  };
}

/**
 * React hook flavor — auto-binds to the active language from useLanguage(),
 * so components don't have to thread `language` through every <YumixTip>.
 *
 * @param {string} key
 * @returns {{id: string, title: string, text: React.ReactNode, language: string}|null}
 */
export function useCoachTip(key) {
  const { language } = useLanguage();
  const tip = getCoachTip(key, language);
  if (!tip) return null;
  return { ...tip, language };
}

/** Returns the list of registered keys — used by tips-audit.js. */
export const REGISTERED_TIP_KEYS = Object.keys(COACH_TIPS);
